import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Loader from '../components/Loader';
import AddGuestModal from '../components/guests/AddGuestModal';
import { motion } from 'framer-motion';
import { FaPlus, FaTrash, FaEdit, FaEye } from 'react-icons/fa';

const Guests = () => {
  const { propertyId } = useParams();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('token');
  const [guestPasswords, setGuestPasswords] = useState({});

  const [guests, setGuests] = useState([]);
  const [role, setRole] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Obtener el rol del usuario en la propiedad
  const fetchPropertyRole = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRole(response.data.role);
    } catch (error) {
      console.error('Error al obtener el rol en la propiedad:', error);
    }
  };

  // Obtener la lista de huéspedes
  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}auth/guests/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setGuests(response.data.guests || []);
    } catch (error) {
      console.error('Error al cargar huéspedes:', error);
    } finally {
      setTimeout(() => setIsLoading(false), 500); // Reducir parpadeo del loader
    }
  };

  useEffect(() => {
    fetchPropertyRole();
    fetchGuests();

    const interval = setInterval(fetchGuests, 5000); // Refrescar cada 5 segundos
    return () => clearInterval(interval);
  }, [API_BASE_URL, propertyId]);

  const handleAddGuest = async (newGuest) => {
    try {
      await axios.post(`${API_BASE_URL}auth/register-guest`, newGuest, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchGuests(); // Actualizar la tabla después de añadir un huésped
    } catch (error) {
      console.error('Error al añadir huésped:', error);
      alert('Hubo un error al añadir el huésped.');
    }
  };

  const handleDeleteGuest = async (guestId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este huésped?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}auth/delete/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchGuests();
    } catch (error) {
      console.error('Error al eliminar huésped:', error);
      alert('Hubo un error al eliminar el huésped.');
    }
  };

  const handleShowPassword = async (guestId) => {
    try {
      if (guestPasswords[guestId]) {
        // Si la contraseña ya está en el estado, la muestra sin hacer petición
        window.alert(`La contraseña del huésped es: ${guestPasswords[guestId]}`);
        return;
      }
      const response = await axios.get(`${API_BASE_URL}auth/get-guest-password/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(response.data.guest.newPassword);

      const password = response.data.guest.newPassword;

      // Guardar la contraseña en el estado para evitar múltiples solicitudes
      setGuestPasswords((prev) => ({
        ...prev,
        [guestId]: password,
      }));

      // Mostrar la contraseña en una alerta
      window.alert(`La contraseña del huésped es: ${password}`);

      // Eliminar la contraseña del estado después de 5 segundos
      setTimeout(() => {
        setGuestPasswords((prev) => {
          const updatedPasswords = { ...prev };
          delete updatedPasswords[guestId];
          return updatedPasswords;
        });
      }, 5000);
    } catch (error) {
      console.error('Error al obtener la contraseña:', error);
      alert('No se pudo obtener la contraseña.');
    }
  };

  return (
    <StyledGuests>
      <motion.h1
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Huéspedes
      </motion.h1>

      {/* Botón de añadir huésped (solo para admin y owner) */}
      {(role === 'admin' || role === 'owner') && (
        <motion.button
          className="add-btn"
          onClick={() => setShowModal(true)}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaPlus className="icon" />
        </motion.button>
      )}

      {/* Loader mientras se obtienen los huéspedes */}
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <div className="container">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {guests.length > 0 ? (
                guests.map((guest) => (
                  <tr key={guest._id}>
                    <td>{guest.name}</td>
                    <td>{guest.email}</td>
                    <td>{new Date(guest.startDate).toLocaleDateString()}</td>
                    <td>{new Date(guest.endDate).toLocaleDateString()}</td>
                    <td className="actions">
                      {/* <button className="edit-btn">
                        <FaEdit />
                      </button> */}
                      <button className="show-btn" onClick={() => handleShowPassword(guest._id)}>
                        <FaEye />
                      </button>
                      <button className="delete-btn" onClick={() => handleDeleteGuest(guest._id)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-data">No hay huéspedes registrados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal para añadir huéspedes */}
      {showModal && (
        <AddGuestModal
          propertyId={propertyId}
          onClose={() => setShowModal(false)}
          onAddGuest={handleAddGuest}
        />
      )}
    </StyledGuests>
  );
};

// Loader centrado
const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
  width: 100%;
`;

// Estilos de la página
const StyledGuests = styled.div`
  padding: 20px;

  h1 {
    margin-bottom: 20px;
    font-size: 2rem;
    text-align: center;
  }

  .add-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    background-color: #008080;
    color: white;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 4px 10px;
    transition: all 0.3s ease;
  }

  .add-btn:hover {
    background-color: rgb(22, 104, 106);
    transform: scale(1.1);
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th, td {
    padding: 10px;
    border-bottom: 1px solid #ddd;
    text-align: left;
  }

  .actions {
    display: flex;
    gap: 10px;
  }

  .edit-btn, .delete-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.2rem;
  }

  .delete-btn {
    color: red;
  }

  .show-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
  }

  .no-data {
    text-align: center;
    font-style: italic;
  }
`;

export default Guests;